import React, { ForwardedRef, forwardRef, PropsWithChildren } from 'react';
import { useMediaIsDesktop } from '@collaborator/npm_modules/dist/components/media';
import { ButtonVariant } from '@collaborator/npm_modules/dist/components/button/button-variant';
import { Size } from '@collaborator/npm_modules/dist/css/size';
import { classNames } from 'components/class-names';
import { PropTypes } from 'components/button/ButtonType';
import { useSweetAlert } from '@collaborator/npm_modules/dist/components/sweetAlert/hooks/useSweetAlert';
import { PopoverStickOnHover } from 'components/popover/PopoverStickOnHover';
import { NavLink } from 'components/router/NavLink';
import { Link } from 'components/router/Link';
import {
  PrettyLoaderConfigType,
  PrettyLoaderReact,
  PrettyLoaderType,
} from '@collaborator/npm_modules/dist/components/pretty-loader';

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, PropsWithChildren<PropTypes>>(({
  children,
  variant,
  variantDesktop,
  variantMobile,
  success,
  primary,
  danger,
  info,
  secondary,
  warning,
  light,
  ivory,
  dangerLight,
  dashed,
  dashedDesktop,
  dashedMobile,
  sizeSm,
  sizeSmDesktop,
  sizeSmMobile,
  sizeLg,
  sizeLgDesktop,
  sizeLgMobile,
  sizeInitial,
  sizeInitialDesktop,
  sizeInitialMobile,
  block,
  blockDesktop,
  blockMobile,
  square,
  squareDesktop,
  squareMobile,
  disabled,
  className = 'btn',
  additionalClassName,
  additionalClassNameDesktop,
  additionalClassNameMobile,
  type = 'button',
  asLink,
  asNavLink,
  to,
  onClick,
  prettyLoaderProps,
  sweetAlertProps,
  sweetAlertOnConfirm,
  popoverStickOnHoverProps,
  ...props
}, ref) => {
  const { show, update, hide } = useSweetAlert();
  const isDesktop = useMediaIsDesktop();

  const handlerConfirm = () => {
    update({
      confirmBtnLoading: true,
    });
    sweetAlertOnConfirm().then(() => {
      hide();
    });
  };

  const handlerClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e);
    }

    if (sweetAlertProps) {
      show({
        onConfirm: handlerConfirm,
        ...sweetAlertProps,
      });
    }
  };

  const getCurrentVariant = (): ButtonVariant => {
    if (isDesktop && variantDesktop) return variantDesktop;
    if (!isDesktop && variantMobile) return variantMobile;
    if (success) return ButtonVariant.success;
    if (primary) return ButtonVariant.primary;
    if (danger) return ButtonVariant.danger;
    if (info) return ButtonVariant.info;
    if (secondary) return ButtonVariant.secondary;
    if (warning) return ButtonVariant.warning;
    if (light) return ButtonVariant.light;
    if (ivory) return ButtonVariant.ivory;
    if (dangerLight) return ButtonVariant.dangerLight;
    return variant;
  };

  const getCurrentSize = (): Size | null => {
    if (isDesktop && sizeLgDesktop) return Size.lg;
    if (!isDesktop && sizeLgMobile) return Size.lg;
    if (isDesktop && sizeSmDesktop) return Size.sm;
    if (!isDesktop && sizeSmMobile) return Size.sm;
    if (sizeLg) return Size.lg;
    if (sizeSm) return Size.sm;
    return null;
  };

  const buttonProps = {
    className: classNames(
      [
        className,
        { [`btn-${getCurrentVariant()}`]: getCurrentVariant() },
        { 'btn-block': block },
        { 'btn-square': square },
        { 'btn-sm': getCurrentSize() === Size.sm },
        { 'btn-lg': getCurrentSize() === Size.lg },
        { 'btn-size-initial': sizeInitial },
        { 'btn--dashed': dashed },
        additionalClassName,
      ],
      [
        { 'btn-block': blockDesktop },
        { 'btn-square': squareDesktop },
        { 'btn-size-initial': sizeInitialDesktop },
        { 'btn-dashed': dashedDesktop },
        additionalClassNameDesktop,
      ],
      [
        { 'btn-block': blockMobile },
        { 'btn-square': squareMobile },
        { 'btn-size-initial': sizeInitialMobile },
        { 'btn--dashed': dashedMobile },
        additionalClassNameMobile,
      ],
    ),
    disabled,
    onClick: handlerClick,
    ...props,
  };

  const wrapPopoverStickOnHover = (button: React.ReactElement): React.ReactElement => {
    if (!popoverStickOnHoverProps) {
      return button;
    }

    return (
      <PopoverStickOnHover
        text={popoverStickOnHoverProps.text}
        placement={popoverStickOnHoverProps.placement}
        onExited={popoverStickOnHoverProps.onExited}
        asHtml={popoverStickOnHoverProps.asHtml}
        popoverProps={popoverStickOnHoverProps.popoverProps}
      >
        <span>
          {button}
        </span>
      </PopoverStickOnHover>
    );
  };

  const wrapPrettyLoader = (button: React.ReactElement) => {
    if (!prettyLoaderProps) {
      return button;
    }

    const config: Partial<PrettyLoaderConfigType> = prettyLoaderProps.config || {};
    if (!config.color) {
      if (getCurrentVariant() === ButtonVariant.primary) {
        config.color = '#858585';
      }
    }
    if (!config.type) {
      if (square) {
        config.type = PrettyLoaderType.circle;
      }
    }

    return (
      <PrettyLoaderReact isLoading={prettyLoaderProps.isLoading} config={config}>
        {button}
      </PrettyLoaderReact>
    );
  };

  if (asLink) {
    return wrapPopoverStickOnHover(
      <Link ref={ref as ForwardedRef<HTMLAnchorElement>} to={to} {...buttonProps}>
        {children}
      </Link>,
    );
  }

  if (asNavLink) {
    return wrapPopoverStickOnHover(
      <NavLink ref={ref as ForwardedRef<HTMLAnchorElement>} to={to} {...buttonProps}>
        {children}
      </NavLink>,
    );
  }

  return wrapPopoverStickOnHover(wrapPrettyLoader(
    <button
      type={type === 'button' ? 'button' : 'submit'}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      {...buttonProps}
    >
      {children}
    </button>,
  ));
});

export { Button };
